import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Balm", to: "/product/balm" },
  { label: "Patches", to: "/product/patch" },
  { label: "Bundles", hash: "bundle-builder" },
  { label: "Subscribe", hash: "subscribe-save" },
  { label: "Reviews", hash: "reviews" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToHash = (hash) => {
    setMobileOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-30 transition-colors duration-300 ${
        scrolled ? "bg-[#F8F5EF]/90 backdrop-blur-md border-b border-[#6F503A]/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-12 h-20 flex items-center justify-between">
        <Logo className="h-8 md:h-9" />

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-[15px] font-medium text-[#6F503A]/80 hover:text-[#6F503A] transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                onClick={() => goToHash(l.hash)}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-[15px] font-medium text-[#6F503A]/80 hover:text-[#6F503A] transition-colors"
              >
                {l.label}
              </button>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="open-cart-btn"
            onClick={() => setOpen(true)}
            className="relative p-3 rounded-full hover:bg-[#E9E2D6] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5 text-[#6F503A]" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-[#6F503A] text-[#F8F5EF] text-[11px] font-semibold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden p-3 rounded-full hover:bg-[#E9E2D6] transition-colors"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5 text-[#6F503A]" /> : <Menu className="w-5 h-5 text-[#6F503A]" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-[#F8F5EF] border-t border-[#6F503A]/10 px-5 py-4" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-[#6F503A] font-medium"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goToHash(l.hash)}
                  className="py-3 text-left text-[#6F503A] font-medium"
                >
                  {l.label}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
};
